import React, { useState, useEffect, useRef, useCallback } from "react";
import confetti from "canvas-confetti";
import { getParticipants } from "./airtableService";
import "./styles.css";

/* ---------------- timing schedule (shared by visuals + audio) ---------------- */
const SPIN_MS = 3300;
const easeOut = (p) => 1 - Math.pow(1 - p, 3);
function buildSchedule(total) {
  const t = [];
  let cur = 0;
  while (cur < total) {
    t.push(cur);
    const p = cur / total;
    cur += 55 + (240 - 55) * easeOut(p);
  }
  return t;
}

/* ---------------- audio (synth, no files) ---------------- */
const Sound = {
  ctx: null,
  master: null,
  ensure() {
    if (!this.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.45;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === "suspended") this.ctx.resume();
  },
  blip(time, freq, dur, vol) {
    if (!this.ctx) return;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = "triangle";
    o.frequency.value = freq;
    g.gain.setValueAtTime(0, time);
    g.gain.linearRampToValueAtTime(vol, time + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, time + dur);
    o.connect(g);
    g.connect(this.master);
    o.start(time);
    o.stop(time + dur + 0.02);
  },
  roll(times) {
    this.ensure();
    if (!this.ctx) return;
    const t0 = this.ctx.currentTime + 0.02;
    times.forEach((ms, i) => {
      const p = i / times.length;
      this.blip(t0 + ms / 1000, 220 + p * 520, 0.08, 0.1 + p * 0.16);
    });
  },
  ding(i) {
    this.ensure();
    if (!this.ctx) return;
    const base = 523.25;
    const f = base * Math.pow(2, i / 5);
    this.blip(this.ctx.currentTime + 0.01, f, 0.45, 0.32);
    this.blip(this.ctx.currentTime + 0.01, f * 1.5, 0.45, 0.14);
  },
  chord() {
    this.ensure();
    if (!this.ctx) return;
    const t = this.ctx.currentTime + 0.02;
    [392.0, 493.88, 587.33, 783.99].forEach((f, i) =>
      this.blip(t + i * 0.04, f, 1.4, 0.22)
    );
  },
};

/* ---------------- confetti palette (derives from your CI red) ---------------- */
function shade(hex, amt) {
  hex = (hex || "").replace("#", "").trim();
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  if (hex.length !== 6) return "#cc1042";
  let r = parseInt(hex.slice(0, 2), 16);
  let g = parseInt(hex.slice(2, 4), 16);
  let b = parseInt(hex.slice(4, 6), 16);
  const t = amt < 0 ? 0 : 255;
  const p = Math.abs(amt);
  r = Math.round((t - r) * p + r);
  g = Math.round((t - g) * p + g);
  b = Math.round((t - b) * p + b);
  return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
}
function brandColors() {
  const ci =
    getComputedStyle(document.documentElement)
      .getPropertyValue("--ci-red")
      .trim() || "#cc1042";
  return [ci, shade(ci, 0.28), shade(ci, -0.22), "#E8B04B"];
}

const LuckyDraw = () => {
  const [pool, setPool] = useState([]); // remaining eligible names
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [round, setRound] = useState(1);
  const [winnerCount, setWinnerCount] = useState(5);
  const [removeWinners, setRemoveWinners] = useState(true);
  const [sound, setSound] = useState(true);

  const [spinning, setSpinning] = useState(false);
  const [litIndex, setLitIndex] = useState(-1);
  const [winners, setWinners] = useState([]);
  const [revealed, setRevealed] = useState(0);
  const [showWinners, setShowWinners] = useState(false);

  const timersRef = useRef([]);
  const wcardRefs = useRef([]);

  // refs mirroring state for use inside scheduled callbacks / key handler
  const soundRef = useRef(sound);
  const spinningRef = useRef(spinning);
  const showWinnersRef = useRef(showWinners);
  const poolRef = useRef(pool);
  const winnerCountRef = useRef(winnerCount);
  const removeWinnersRef = useRef(removeWinners);
  useEffect(() => { soundRef.current = sound; }, [sound]);
  useEffect(() => { spinningRef.current = spinning; }, [spinning]);
  useEffect(() => { showWinnersRef.current = showWinners; }, [showWinners]);
  useEffect(() => { poolRef.current = pool; }, [pool]);
  useEffect(() => { winnerCountRef.current = winnerCount; }, [winnerCount]);
  useEffect(() => { removeWinnersRef.current = removeWinners; }, [removeWinners]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const fetchParticipants = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const fetched = await getParticipants();
      const clean = (fetched || []).filter(Boolean);
      setPool(clean);
      setRound(1);
      setShowWinners(false);
      setWinners([]);
      setLitIndex(-1);
    } catch (e) {
      setError("Failed to fetch participants. Please try again.");
      console.error("Error fetching participants:", e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchParticipants();
    return clearTimers;
  }, [fetchParticipants]);

  const triggerBurst = (x, y, count) => {
    confetti({
      particleCount: count || 60, spread: 70, startVelocity: 42, gravity: 1.05,
      scalar: 1.05, ticks: 220, colors: brandColors(), shapes: ["circle", "square"],
      origin: { x, y },
    });
  };
  const finale = () => {
    const colors = brandColors();
    confetti({ particleCount: 120, angle: 60, spread: 75, startVelocity: 60, colors, shapes: ["circle", "square"], origin: { x: 0, y: 0.85 } });
    confetti({ particleCount: 120, angle: 120, spread: 75, startVelocity: 60, colors, shapes: ["circle", "square"], origin: { x: 1, y: 0.85 } });
    confetti({ particleCount: 90, spread: 120, startVelocity: 45, scalar: 1.2, colors, shapes: ["circle", "square"], origin: { x: 0.5, y: 0.4 } });
  };

  const reveal = () => {
    const currentPool = poolRef.current;
    const n = Math.min(winnerCountRef.current, currentPool.length);
    const copy = currentPool.slice();
    const picked = [];
    for (let i = 0; i < n; i++) {
      const r = Math.floor(Math.random() * copy.length);
      picked.push(copy.splice(r, 1)[0]);
    }
    wcardRefs.current = [];
    setWinners(picked);
    setRevealed(0);
    setShowWinners(true);

    picked.forEach((_, i) => {
      timersRef.current.push(
        setTimeout(() => {
          setRevealed((v) => Math.max(v, i + 1));
          if (soundRef.current) Sound.ding(i);
          const el = wcardRefs.current[i];
          if (el) {
            const rect = el.getBoundingClientRect();
            triggerBurst(
              (rect.left + rect.width / 2) / window.innerWidth,
              (rect.top + rect.height / 2) / window.innerHeight,
              55
            );
          }
        }, 350 + i * 520)
      );
    });

    timersRef.current.push(
      setTimeout(() => {
        finale();
        if (soundRef.current) Sound.chord();
      }, 350 + picked.length * 520 + 150)
    );

    if (removeWinnersRef.current) {
      setPool((prev) => prev.filter((p) => !picked.includes(p)));
    }
    setSpinning(false);
  };

  const spin = () => {
    if (spinningRef.current || poolRef.current.length < 1) return;
    setSpinning(true);
    setLitIndex(-1);
    if (soundRef.current) Sound.ensure();
    const schedule = buildSchedule(SPIN_MS);
    if (soundRef.current) Sound.roll(schedule);

    const n = poolRef.current.length;
    schedule.forEach((ms, i) => {
      timersRef.current.push(setTimeout(() => setLitIndex(i % n), ms));
    });
    timersRef.current.push(
      setTimeout(() => {
        setLitIndex(-1);
        reveal();
      }, SPIN_MS + 120)
    );
  };

  const closeOverlay = () => {
    setShowWinners(false);
    setLitIndex(-1);
    setRound((r) => r + 1);
  };

  const toggleFs = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  // presenter keyboard shortcuts (Space=spin, Esc=close, F=fullscreen, M=mute)
  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space" || e.code === "Enter") {
        if (showWinnersRef.current) return;
        e.preventDefault();
        spin();
      } else if (e.key === "Escape" && showWinnersRef.current) {
        closeOverlay();
      } else if (e.key === "f" || e.key === "F") {
        toggleFs();
      } else if (e.key === "m" || e.key === "M") {
        setSound((s) => !s);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const canSpin = pool.length >= 1 && !spinning;
  const displayCount = Math.min(winnerCount, Math.max(1, pool.length));

  return (
    <div className="stage">
      <div className="topbar">
        <div className="brand">
          <img
            className="brand-logo"
            src="https://wiki.richproductsmy.kitchen/wp-content/uploads/2025/03/richs_logo_lockup_horiz.png"
            alt="Rich's"
          />
        </div>
        <div className="utils">
          <button
            className={"util" + (sound ? "" : " off")}
            title="Sound on/off (M)"
            onClick={() => setSound((s) => !s)}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 9v6h4l5 5V4L7 9H3z" />
              <path d="M16 8a5 5 0 0 1 0 8" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
          </button>
          <button className="util" title="Fullscreen (F)" onClick={toggleFs}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
            </svg>
          </button>
        </div>
      </div>

      <div className="headline">
        <div className="kicker">Dare to Dream</div>
        <h1 className="title">Lucky Draw</h1>
        <div className="rule" />
        <div className="meta">
          <span className="round">Round {String(round).padStart(2, "0")}</span>
          <span className="pill">
            <span className="dot" />
            <b>{pool.length}</b>&nbsp;names in the draw
          </span>
        </div>
      </div>

      <div className="pool-wrap">
        {isLoading ? (
          <p className="empty">Loading participants…</p>
        ) : error ? (
          <p className="empty" style={{ color: "var(--ci-red)" }}>{error}</p>
        ) : pool.length === 0 ? (
          <p className="empty">Everyone has won — refresh to start a fresh pool.</p>
        ) : (
          <div className={"pool" + (showWinners ? " dimmed" : "")}>
            {pool.map((name, i) => (
              <div key={name + i} className={"name" + (i === litIndex ? " lit" : "")}>
                {name}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="controls">
        <button className={"spin" + (spinning ? " busy" : "")} onClick={spin} disabled={!canSpin}>
          {spinning && <span className="spinner">⟳</span>}
          <span>{spinning ? "Drawing…" : "Spin the Names"}</span>
        </button>
        <div className="settings">
          <div className="set">
            Winners
            <span className="stepper">
              <button onClick={() => setWinnerCount((c) => Math.max(1, c - 1))}>−</button>
              <b>{displayCount}</b>
              <button onClick={() => setWinnerCount((c) => Math.min(10, c + 1))}>+</button>
            </span>
          </div>
          <div className="set">
            Remove past winners
            <span
              className={"switch" + (removeWinners ? " on" : "")}
              onClick={() => setRemoveWinners((v) => !v)}
            />
          </div>
          <div className="set">
            Sound
            <span
              className={"switch" + (sound ? " on" : "")}
              onClick={() => setSound((v) => !v)}
            />
          </div>
        </div>
      </div>

      <div className={"winners" + (showWinners ? " show" : "")}>
        <div>
          <h2>
            Our <em>Winners</em>
          </h2>
          <div className="sub">Congratulations &amp; selamat!</div>
        </div>
        <div className="wcards">
          {winners.map((name, i) => (
            <div
              key={name + i}
              ref={(el) => (wcardRefs.current[i] = el)}
              className={"wcard" + (i < revealed ? " in" : "")}
            >
              <div className="rank">{i + 1}</div>
              <div className="wname">{name}</div>
              <div className="wlabel">Winner</div>
            </div>
          ))}
        </div>
        <button className="again" onClick={closeOverlay}>
          Draw Again
        </button>
      </div>
    </div>
  );
};

export default LuckyDraw;
